import React, { Fragment, useLayoutEffect, useRef, useState } from 'react';
import ActionMenu from './ActionMenu';
import Button from './Button';
import { buildActionBarMenuItems } from './actionBarMenuItems';

type BaseButtonProps = React.ComponentProps<typeof Button>;

export interface ActionBarItem {
    label: string;
    onClick?: () => void;
    disabled?: boolean;
    icon?: React.ReactNode;
    tooltip?: string;
    variant?: BaseButtonProps['variant'];
    size?: BaseButtonProps['size'];
    radius?: BaseButtonProps['radius'];
    /** Renders a vertical rule before this button (and a menu divider when collapsed). */
    dividerBefore?: boolean;
}

export interface ActionBarProps {
    buttons: ActionBarItem[];
    /** Container width, in px, below which the row collapses into a single menu. */
    collapseBelow?: number;
    /** Accessible name for the collapsed menu trigger. */
    menuLabel?: string;
    className?: string;
}

const DEFAULT_COLLAPSE_BELOW_PX = 600;


const ActionBar: React.FC<ActionBarProps> = ({
    buttons,
    collapseBelow = DEFAULT_COLLAPSE_BELOW_PX,
    menuLabel = 'Actions',
    className = '',
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isCompact, setIsCompact] = useState(false);

    useLayoutEffect(() => {
        const findStableContainer = (start: HTMLElement | null): HTMLElement | null => {
            let node: HTMLElement | null = start;

            for (let depth = 0; node && depth < 5; depth += 1) {
                const parent = node.parentElement;
                if (!parent) {
                    return node;
                }
                if (parent.clientWidth > node.clientWidth) {
                    return parent;
                }
                node = parent;
            }

            return node;
        };

        const element = findStableContainer(containerRef.current);
        if (!element) {
            return;
        }

        const applyWidth = (width: number) => {
            setIsCompact(width > 0 && width < collapseBelow);
        };

        applyWidth(element.clientWidth);

        if (typeof ResizeObserver === 'undefined') {
            return;
        }

        let lastWidth = element.clientWidth;

        const observer = new ResizeObserver((entries) => {
            const width = entries[0]?.contentRect.width;
            if (width === undefined || Math.abs(width - lastWidth) < 1) {
                return;
            }
            lastWidth = width;
            applyWidth(width);
        });
        observer.observe(element);
        return () => observer.disconnect();
    }, [collapseBelow]);

    if (buttons.length === 0) {
        return null;
    }

    return (
        <div ref={containerRef} className={className}>
            {isCompact ? (
                <ActionMenu items={buildActionBarMenuItems(buttons)} label={menuLabel} />
            ) : (
                <div className="flex items-center gap-2">
                    {buttons.map((button, index) => (
                        <Fragment key={`${button.label}-${index}`}>
                            {button.dividerBefore && index > 0 && (
                                <div
                                    aria-hidden="true"
                                    className="w-px h-4 shrink-0 bg-[var(--malachite-card-border)]"
                                />
                            )}
                            <Button
                                variant={button.variant ?? 'filled'}
                                size={button.size}
                                radius={button.radius}
                                onClick={button.onClick}
                                disabled={button.disabled}
                                icon={button.icon}
                                tooltip={button.tooltip}
                            >
                                {button.label}
                            </Button>
                        </Fragment>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ActionBar;
